import { FirebaseError } from "firebase/app";
import { multiFactor, TotpMultiFactorGenerator, type MultiFactorInfo, type User as FirebaseUser } from "firebase/auth";

/**
 * Browser-side half of portal two-factor (AIR-1500): the attestation round
 * trip and the Firebase SDK lookups the challenge, profile and login
 * surfaces share. Pure functions over the Firebase user — no React here.
 */

/** Issuer shown in authenticator apps next to the account email. */
export const MFA_ISSUER = "AI Reserve";

/** Display name recorded on the enrolled factor. */
export const MFA_FACTOR_DISPLAY_NAME = "Authenticator app";

export const MFA_ERROR_CODES = {
  multiFactorRequired: "auth/multi-factor-auth-required",
  requiresRecentLogin: "auth/requires-recent-login",
  invalidCode: "auth/invalid-verification-code",
  tooManyRequests: "auth/too-many-requests",
  popupClosed: "auth/popup-closed-by-user",
  popupCancelled: "auth/cancelled-popup-request",
  wrongPassword: "auth/wrong-password",
  invalidCredential: "auth/invalid-credential",
  userMismatch: "auth/user-mismatch",
} as const;

export function isFirebaseError(error: unknown, code: string): boolean {
  return error instanceof FirebaseError && error.code === code;
}

/** A 6-digit code exactly, the only shape an authenticator app produces. */
export function isTotpCode(value: string): boolean {
  return /^\d{6}$/.test(value);
}

/** Keep only digits, capped at six — lets users paste "123 456" from a manager. */
export function normalizeTotpInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function totpFactorOf(user: FirebaseUser): MultiFactorInfo | undefined {
  return multiFactor(user).enrolledFactors.find(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
}

export function hasTotpEnrolled(user: FirebaseUser): boolean {
  return totpFactorOf(user) !== undefined;
}

/** SSO accounts are covered by their identity provider's own second factor. */
export function isSsoFirebaseUser(user: FirebaseUser): boolean {
  return user.providerData.some((profile) => profile.providerId.startsWith("saml."));
}

export type ReauthMethod = "password" | "google.com" | "microsoft.com";

/** The ways this account can prove it is still the same person, in display order. */
export function reauthMethodsOf(user: FirebaseUser): ReauthMethod[] {
  const providers = new Set(user.providerData.map((profile) => profile.providerId));
  const methods: ReauthMethod[] = [];
  if (providers.has("password")) methods.push("password");
  if (providers.has("google.com")) methods.push("google.com");
  if (providers.has("microsoft.com")) methods.push("microsoft.com");
  return methods;
}

export class MfaAttestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = "MfaAttestError";
    this.status = status;
  }
}

/**
 * Prove to the server that this sign-in passed (or just enrolled) a second
 * factor. Resolves true when the attestation cookie was minted, false when
 * the token carries nothing to attest (403 — the usual answer for accounts
 * without a second factor), and throws for anything else so callers can
 * surface it like a failed session sync.
 */
export async function attestMfa(idToken: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  let response: Response;
  try {
    response = await fetchImpl("/api/mfa/attest", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
  } catch (error) {
    throw new MfaAttestError(error instanceof Error ? error.message : "Network error", null);
  }
  if (response.status === 204) return true;
  if (response.status === 403) return false;
  throw new MfaAttestError(`Two-factor attestation failed with HTTP ${response.status}`, response.status);
}
